/**
 * Service layer for the agentic-analytics experiment dashboards API.
 * Raw fetchers that return wire shapes verbatim — UI mappers live alongside
 * the consuming hook (`use-experiment-analytics`) so the wire types stay
 * source-of-truth for what the backend sends.
 */

import { analyticsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import { extractDispositionFilename, triggerBrowserDownload } from '~/utils/download-file';

import type {
  AnalyticsArmComparisonResponse,
  AnalyticsDailyLiftResponse,
  AnalyticsDataDownloadsResponse,
  AnalyticsExperimentAgentSummary,
  AnalyticsExperimentDetailParams,
  AnalyticsExperimentHealthResponse,
  AnalyticsExperimentListParams,
  AnalyticsExperimentListResponse,
  AnalyticsExperimentSummary,
  AnalyticsPerformanceTrendResponse,
  AnalyticsRoasComparisonResponse,
  AnalyticsTimelineCheckpoint,
} from '../types/analytics.types';
import type {
  AdjustedMetricsResponse,
  CategoryExclusionsResponse,
  DataDownloadCard,
  HealthInStockTrends,
  PerformanceMetric,
} from '../types/experiment.types';

/**
 * Build a Ky-compatible `searchParams` value. Primitives stringify cleanly;
 * primitive arrays expand into repeated `key=value` pairs so multi-status
 * filters round-trip. Anything else is skipped — and a dev-only warning
 * fires so a future caller adding a Date / object value notices it instead
 * of seeing it silently drop from the URL.
 */
function buildSearchParams(params: object | undefined): URLSearchParams {
  const out = new URLSearchParams();
  if (!params) return out;
  const isPrimitive = (v: unknown): v is string | number | boolean =>
    typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null) continue;
    if (isPrimitive(value)) {
      out.append(key, String(value));
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        if (isPrimitive(item)) {
          out.append(key, String(item));
        } else if (import.meta.env.DEV) {
          console.warn(
            `[analytics.service] buildSearchParams: dropped non-primitive item in array param "${key}"`,
            item,
          );
        }
      }
      continue;
    }
    if (import.meta.env.DEV) {
      console.warn(
        `[analytics.service] buildSearchParams: dropped non-primitive value for "${key}"`,
        value,
      );
    }
  }
  return out;
}

/**
 * Ky options carrying TanStack Query's abort signal, so an unmounted or
 * superseded analytics read is cancelled instead of running on to the timeout.
 * Omitted when absent (`exactOptionalPropertyTypes` rejects `signal: undefined`).
 */
export function withSignal(signal?: AbortSignal): { signal?: AbortSignal } {
  return signal ? { signal } : {};
}

/** Paginated portfolio list with augmented `lift_pct` + `needs_attention`. */
export function fetchAnalyticsExperimentList(
  params?: AnalyticsExperimentListParams,
  signal?: AbortSignal,
): Promise<AnalyticsExperimentListResponse> {
  return analyticsClient
    .get(endpoints.analytics.experiments.list(), {
      searchParams: buildSearchParams(params),
      ...withSignal(signal),
    })
    .json<AnalyticsExperimentListResponse>();
}

/**
 * Aggregated KPI summary for the Performance-tab header. `segment` is the
 * encoded `?segment=` product filter (see `encodeSegmentFilter`) — omitted
 * for the unscoped, full-cohort result.
 */
export function fetchAnalyticsExperimentSummary(
  id: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<AnalyticsExperimentSummary> {
  return analyticsClient
    .get(endpoints.analytics.experiments.summary(id), {
      searchParams: buildSearchParams(segment ? { segment } : undefined),
      ...withSignal(signal),
    })
    .json<AnalyticsExperimentSummary>();
}

/** Day-0 → Day-N+ext checkpoints for the experiment-detail Timeline tab. */
export function fetchAnalyticsTimeline(
  id: string,
  signal?: AbortSignal,
): Promise<AnalyticsTimelineCheckpoint[]> {
  return analyticsClient
    .get(endpoints.analytics.experiments.timeline(id), withSignal(signal))
    .json<AnalyticsTimelineCheckpoint[]>();
}

/** Per-arm cumulative lift trend, already computed server-side for the requested metric + segment. */
export function fetchAnalyticsPerformanceTrend(
  id: string,
  metric: PerformanceMetric,
  segment?: string,
  signal?: AbortSignal,
): Promise<AnalyticsPerformanceTrendResponse> {
  return analyticsClient
    .get(endpoints.analytics.experiments.performanceTrend(id), {
      searchParams: buildSearchParams({ metric, segment }),
      ...withSignal(signal),
    })
    .json<AnalyticsPerformanceTrendResponse>();
}

/** Health flags + outlier breakdown for the chosen sensitivity level. */
export function fetchAnalyticsExperimentHealth(
  id: string,
  params?: AnalyticsExperimentDetailParams,
  signal?: AbortSignal,
): Promise<AnalyticsExperimentHealthResponse> {
  return analyticsClient
    .get(endpoints.analytics.experiments.health(id), {
      searchParams: buildSearchParams(params),
      ...withSignal(signal),
    })
    .json<AnalyticsExperimentHealthResponse>();
}

/** Pre-digested context for the Lily Agent panel (headline + notes). */
export function fetchAnalyticsAgentSummary(
  id: string,
  params?: AnalyticsExperimentDetailParams,
  signal?: AbortSignal,
): Promise<AnalyticsExperimentAgentSummary> {
  return analyticsClient
    .get(endpoints.analytics.experiments.agentSummary(id), {
      searchParams: buildSearchParams(params),
      ...withSignal(signal),
    })
    .json<AnalyticsExperimentAgentSummary>();
}

/** Side-by-side per-arm averages on 5 canonical metrics, with winner. */
export function fetchAnalyticsArmComparison(
  id: string,
  params?: AnalyticsExperimentDetailParams,
  segment?: string,
  signal?: AbortSignal,
): Promise<AnalyticsArmComparisonResponse> {
  return analyticsClient
    .get(endpoints.analytics.experiments.armComparison(id), {
      searchParams: buildSearchParams({ ...params, segment }),
      ...withSignal(signal),
    })
    .json<AnalyticsArmComparisonResponse>();
}

/** Two-source ROAS table (Google Ads today; GA4 joins when ingestion ships). */
export function fetchAnalyticsRoasComparison(
  id: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<AnalyticsRoasComparisonResponse> {
  return analyticsClient
    .get(endpoints.analytics.experiments.roasComparison(id), {
      searchParams: buildSearchParams(segment ? { segment } : undefined),
      ...withSignal(signal),
    })
    .json<AnalyticsRoasComparisonResponse>();
}

/** Per-day rows + per-arm revenue trend for the Daily DID Lift card. */
export function fetchAnalyticsDailyLift(
  id: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<AnalyticsDailyLiftResponse> {
  return analyticsClient
    .get(endpoints.analytics.experiments.dailyLift(id), {
      searchParams: buildSearchParams(segment ? { segment } : undefined),
      ...withSignal(signal),
    })
    .json<AnalyticsDailyLiftResponse>();
}

// Health-tab detail panels. The analytics wire shapes match the UI types
// verbatim (camelCase included), so these fetchers pass the body through with
// no remapping — the service-layer wrappers just add the mock-mode gate.

/** Daily in-stock (serving-availability) trend, test vs control. */
export function fetchAnalyticsHealthInStock(
  id: string,
  signal?: AbortSignal,
): Promise<HealthInStockTrends> {
  return analyticsClient
    .get(endpoints.analytics.experiments.healthInStock(id), withSignal(signal))
    .json<HealthInStockTrends>();
}

/** Pre/post-period metric volumes (DiD transparency table). */
export function fetchAnalyticsHealthCategoryExclusions(
  id: string,
  signal?: AbortSignal,
): Promise<CategoryExclusionsResponse> {
  return analyticsClient
    .get(endpoints.analytics.experiments.healthCategoryExclusions(id), withSignal(signal))
    .json<CategoryExclusionsResponse>();
}

/** Per-arm pre/post-period revenue + product counts (DiD baseline). */
export function fetchAnalyticsHealthAdjustedMetrics(
  id: string,
  signal?: AbortSignal,
): Promise<AdjustedMetricsResponse> {
  return analyticsClient
    .get(endpoints.analytics.experiments.healthAdjustedMetrics(id), withSignal(signal))
    .json<AdjustedMetricsResponse>();
}

/** CSV report tiles for the category-mode Detailed Data Downloads grid. Fetched
 *  independently of `/category-performance` so the grid isn't gated on a category pick. */
export async function fetchAnalyticsDataDownloads(
  id: string,
  segment?: string,
  signal?: AbortSignal,
): Promise<DataDownloadCard[]> {
  const api = await analyticsClient
    .get(endpoints.analytics.experiments.dataDownloads(id), {
      searchParams: buildSearchParams(segment ? { segment } : undefined),
      ...withSignal(signal),
    })
    .json<AnalyticsDataDownloadsResponse>();
  return api.downloads;
}

/**
 * Opt Ky back into retry for the imperative blob downloads below. These have no
 * TanStack Query layer behind them and the client disables retry globally (see
 * client.ts), so without this a transient blip surfaces on the first failure.
 */
const DOWNLOAD_RETRY = { limit: 2, methods: ['get'], statusCodes: [408, 429, 500, 502, 503, 504] };

/**
 * Trigger a CSV download for the experiment performance time-series.
 *
 * Implementation note: we cannot use `window.location.href = url` because the
 * user's Bearer JWT lives in memory (`auth.store`) and is only attached to
 * outbound requests by Ky's `bearerInterceptor`. A browser navigation carries
 * cookies, not headers, so navigating to the analytics URL would hit the
 * gateway with no `Authorization` and no `X-Subsidiary-ID` — best case 401,
 * worst case a cross-tenant fetch. Instead we go through `analyticsClient`
 * (interceptors run), read the body as a `Blob`, and drive a synthetic
 * anchor click on the resulting object URL. The server's
 * `Content-Disposition: attachment; filename=...` is honored when present;
 * otherwise we fall back to a deterministic local name.
 */
export async function downloadAnalyticsExperimentCsv(
  id: string,
  params?: { grain?: 'day' | 'week' | 'month' | undefined; segment?: string | undefined },
): Promise<void> {
  const response = await analyticsClient.get(endpoints.analytics.experiments.exportCsv(id), {
    searchParams: buildSearchParams(params),
    retry: DOWNLOAD_RETRY,
  });
  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `experiment-${id}.csv`;
  triggerBrowserDownload(blob, filename);
}

/**
 * Download a single report tile's CSV from the category-mode Detailed Data
 * Downloads grid. `reportType` is the tile's `id` from
 * `fetchAnalyticsDataDownloads` (e.g. "l2-category-did"). Same imperative
 * download mechanics as `downloadAnalyticsExperimentCsv`; opts back into Ky
 * retry for the same reason.
 */
export async function downloadAnalyticsDataDownloadCsv(
  id: string,
  reportType: string,
  segment?: string,
): Promise<void> {
  const response = await analyticsClient.get(
    endpoints.analytics.experiments.dataDownloadCsv(id, reportType),
    {
      searchParams: buildSearchParams(segment ? { segment } : undefined),
      retry: DOWNLOAD_RETRY,
    },
  );
  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `experiment-${id}-${reportType}.csv`;
  triggerBrowserDownload(blob, filename);
}

/**
 * Download the 28-day results readout as a self-contained 9-slide HTML deck.
 * Download-only by design: the app's CSP sets `frame-src 'none'` and
 * `script-src 'self'`, so the deck's inline navigation/theme script can't run
 * in an iframe or a `blob:` tab — it only works as a saved file.
 */
export async function downloadExperimentReadoutDeck(id: string): Promise<void> {
  const response = await analyticsClient.get(endpoints.analytics.experiments.readoutDeck(id), {
    retry: DOWNLOAD_RETRY,
  });
  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `experiment-${id}-readout-deck.html`;
  triggerBrowserDownload(blob, filename);
}

/** Download the weekly difference-in-differences trend CSV backing the 28-day readout. */
export async function downloadExperimentDidTrendCsv(id: string): Promise<void> {
  const response = await analyticsClient.get(endpoints.analytics.experiments.didTrendCsv(id), {
    retry: DOWNLOAD_RETRY,
  });
  const blob = await response.blob();
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `experiment-${id}-did-trend.csv`;
  triggerBrowserDownload(blob, filename);
}
